import { useState } from 'react';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { LabelModel } from '../../../../../../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaGetGmailLabels,
  getUserQuery,
} from '../../../../../api/users-api';
import AddGmailLabelDialog from './add-label';
import EditGmailLabelDialog from './edit-label';
import DeleteLabelsDialog from './delete-labels.dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { Messages } from '../../../../../utils/message-constants';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Edit from '@mui/icons-material/Edit';
import Mail from '@mui/icons-material/Mail';

export default function LabelsGrid() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();
  const { data: user } = useQuery(getUserQuery(userEmail));

  const queryClient = useQueryClient();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openAddLabel, setOpenAddLabel] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<null | LabelModel>(null);
  const [openEditLabel, setOpenEditLabel] = useState(false);
  const [openDeleteLabels, setOpenDeleteLabels] = useState(false);

  const {
    data: gmailLabels,
    isLoading,
    isFetching: gmailLabelsFetching,
  } = useQuery({
    queryKey: [UserKeys.GmailLabels, userEmail],
    queryFn: () => betaGetGmailLabels(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('gmail labels'),
    },
  });

  const columns: GridColDef<LabelModel>[] = [
    {
      field: 'name',
      headerName: 'Label Name',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'labelVisibility',
      headerName: 'Label Visibility',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      display: 'flex',
      renderCell: (params) => {
        if (params.value === 'labelShow') {
          return 'Show';
        } else if (params.value === 'labelHide') {
          return 'Hide';
        } else if (params.value === 'labelShowIfUnread') {
          return 'Label Show If Unread';
        }
      },
    },
    {
      field: 'messageVisibility',
      headerName: 'Message Visibility',
      editable: false,
      sortable: false,
      filterable: false,
      flex: 1,
      display: 'flex',

      renderCell: (params) => {
        if (params.value === 'show') {
          return 'Show';
        } else if (params.value === 'hide') {
          return 'Hide';
        }
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            key={params.row.id}
            style={{
              display: isAbleToSeeUIElement(
                userRoles,
                UserRoles.EditGmailLabel.value
              )
                ? 'inherit'
                : 'none',
            }}
            label="Edit"
            onClick={() => {
              setSelectedLabel(params.row);
              setOpenEditLabel(true);
            }}
            icon={
              <Tooltip arrow title="Edit">
                <Edit />
              </Tooltip>
            }
          />,
        ];
      },
    },
  ];

  const userLabels =
    gmailLabels
      ?.filter((label) => label.type === 'user')
      .sort((a, b) => a.name.localeCompare(b.name)) || [];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddLabel(true)}
            disabled={
              !isAbleToSeeUIElement(userRoles, UserRoles.AddGmailLabel.value)
            }
            label="Add Label"
            tooltipLabel={
              !isAbleToSeeUIElement(userRoles, UserRoles.AddGmailLabel.value)
                ? Messages.Errors.Permission.Acting(
                    UserRoles.AddGmailLabel.name
                  )
                : 'Add Label'
            }
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteLabels(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.DeleteGmailLabel.value
                )
              }
              tooltipLabel={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.DeleteGmailLabel.value
                )
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.DeleteGmailLabel.name
                    )
                  : 'Delete'
              }
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.GmailLabels, userEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Gmail Labels', <Mail />),
        }}
        rows={userLabels}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        toolbarQuickFilterWidth={400}
        loading={isLoading || gmailLabelsFetching}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        disableColumnSelector
        checkboxSelection
        disableColumnMenu
      />
      <AddGmailLabelDialog
        open={openAddLabel}
        handleClose={() => setOpenAddLabel(false)}
        userEmail={userEmail}
        userLabels={userLabels}
        changeLogRoles={changeLogRoles}
      />
      {selectedLabel ? (
        <EditGmailLabelDialog
          open={openEditLabel}
          handleClose={() => {
            setSelectedLabel(null);
            setOpenEditLabel(false);
          }}
          userEmail={userEmail}
          userLabels={userLabels}
          selectedLabel={selectedLabel}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedRows.length > 0 ? (
        <DeleteLabelsDialog
          open={openDeleteLabels}
          handleClose={() => setOpenDeleteLabels(false)}
          onDeleteSuccess={() => setSelectedRows([])}
          selectedRows={selectedRows}
          userEmail={userEmail}
          userLabels={userLabels}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
